import React, {useEffect, useMemo} from "react";
import {z, ZodObject} from "zod";
import {Client} from "@/Stub/V1";
import {useForm} from "react-hook-form";
import {zodResolver} from "@hookform/resolvers/zod";
import {useValidationErrors} from "@/Hooks/useValidationErrors";
import {clsx} from "clsx";
import BaseTextInput from "@/Components/Forms/Form/BaseTextInput";
import {Button} from "primereact/button";


const ClientFormSchema = z.object({
    name: z.string({
        required_error: 'Name is required'
    }),
    sso_domain: z
        .string()
        .nullish(),
});

export type ClientFormData = z.infer<typeof ClientFormSchema>;


export type ClientFormFormProps = {
    className?: string
    onSubmit?: (data: ClientFormData) => void
    onSubmitButtonLabel?: string
    onSubmitButtonDisabled?: boolean
    isProcessing?: boolean
    initFormData?: Client
    errorBag?: Error[]
};


const ClientForm: React.FC<ClientFormFormProps> = ({
                                                       className,
                                                       onSubmit,
                                                       isProcessing,
                                                       initFormData,
                                                       onSubmitButtonLabel,
                                                       onSubmitButtonDisabled = false,
                                                       errorBag
                                                   }: ClientFormFormProps) => {
    const formInitialValues = useMemo(() => {
        if (initFormData) {
            return {
                ...initFormData,
                name: initFormData.name,
                sso_domain: initFormData?.sso_domain ?? '',
            };
        }

        return {
            name: '',
            sso_domain: ''
        };
    }, [initFormData]);

    const {
        control,
        setError,
        reset,
        formState: {errors},
        handleSubmit,
    } = useForm<ClientFormData>({
        resolver: zodResolver(ClientFormSchema),
        defaultValues: {...formInitialValues}
    });

    const validate = (data: ClientFormData, schema: ZodObject<never>) => {
        return schema.safeParse(data).success;
    }

    const onFormSubmit = (data: ClientFormData) => {
        if (onSubmit && validate(data, ClientFormSchema)) {
            onSubmit(data);
        }
    };

    useEffect(() => {
        if (initFormData) {
            reset({
                ...initFormData,
                name: initFormData.name,
                sso_domain: initFormData?.sso_domain ?? '',
            });
        } else {
            reset({
                name: '',
                sso_domain: ''
            });
        }
    }, [initFormData, reset]);

    useValidationErrors(setError, errorBag);

    return (
        <div className={clsx(className, 'user-form')}>
            <form onSubmit={handleSubmit(onFormSubmit)}>
                <div className="grid">
                    <h4 className={'col-12 my-0 font-bold'}>Client info</h4>
                    <BaseTextInput
                        className={"col-6 rounded-md"}
                        pt={{inputText: {className: 'w-full'}}}
                        control={control}
                        required
                        name={"name"}
                        label={'Name'}
                        errorMessages={errors.name?.message}
                    />
                    <BaseTextInput
                        className={"col-6 rounded-md"}
                        pt={{inputText: {className: 'w-full'}}}
                        control={control}
                        name={"sso_domain"}
                        label={'SSO Domain'}
                        errorMessages={errors.sso_domain?.message}
                    />
                </div>
                <div className="flex mt-4">
                    <Button
                        className={"ml-auto"}
                        label={onSubmitButtonLabel}
                        disabled={onSubmitButtonDisabled}
                        type={"submit"}
                        severity={"success"}
                        loading={isProcessing}
                    />
                </div>
            </form>
        </div>
    );
};

export default ClientForm;